import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { deleteOrderIDB } from 'src/services/offline/outboxClient';

import { Api } from 'src/models/Api.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';
import { CompletedReasonEnum, Session } from 'src/models/Session.model';

export default function useActionAnalyzeModel(
  handleCloseAnalyzingProgressModal: () => void,
  handleResetSession: () => void,
  session: Session,
  setHasConnectionLost: (hasConnectionLost: boolean) => void,
) {
  const history = useHistory();

  const { t } = useTranslation(I18NextNS.COMMON);

  const getOrderStatus = useCallback((completedReason: CompletedReasonEnum) => {
    if (completedReason === CompletedReasonEnum.COMPLETED_EXAM) {
      return OrderStatusEnum.PENDING;
    }

    return OrderStatusEnum.CLOSED;
  }, []);

  const closeExam = async (completedReason: CompletedReasonEnum) => {
    try {
      const [sessionData, orderData] = await Promise.all([
        Api.remote.load_session({
          deviceDataId: session.iid,
        }),
        Api.remote.get_exam_order({
          groupElements: [
            {
              key: 'OrderId',
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
              value: session.orderId,
            },
          ],
        }),
      ]);
      if (!sessionData) {
        throw new Error(t('message.error.failedToLoadSession'));
      }

      if (sessionData.completedTime) {
        throw new Error(
          `${t('message.error.sessionAlreadyCompleted')} ${t(
            `examFields.completedReasonEnum.${completedReason}`,
          )}`,
        );
      }

      if (!orderData.data.content) {
        throw new Error(t('message.error.orderNotFound'));
      }

      const orderStatus = getOrderStatus(completedReason);

      await Promise.all([
        Api.remote.noRetry.save_session(session.iid, {
          completedReason,
          completedTime: new Date(),
        }),
        Api.remote.noRetry.update_exam_order(orderData.data.content[0], {
          status: orderStatus,
        }),
      ]);

      handleResetSession();

      if (!session.orderId) {
        throw new Error(t('message.error.orderNotFound'));
      }
      await deleteOrderIDB(session.orderId);

      return true;
    } catch {
      setHasConnectionLost(true);
      return false;
    }
  };

  const backToOrders = () => {
    handleCloseAnalyzingProgressModal();
    history.push('/session-form');
  };

  const handleCompleteExam = async () => {
    const ok = await closeExam(CompletedReasonEnum.COMPLETED_EXAM);
    if (ok) {
      backToOrders();
    }
    return ok;
  };

  const handleSaveAndDilate = () => {
    handleCloseAnalyzingProgressModal();
    handleResetSession();

    history.push('/results');
  };

  const cancelExam = async () => {
    return closeExam(CompletedReasonEnum.END_SESSION);
  };

  return {
    handleCompleteExam,
    handleSaveAndDilate,
    cancelExam,
    backToOrders,
  };
}
